
import { useState, useEffect, useCallback } from 'react';
import { userPreferencesService, UserPreferences } from '@/services/userPreferencesService';
import { useAuth } from '@/contexts/AuthContext';

const defaultPreferences: UserPreferences = {
  theme: 'light',
  auto_save_interval: 3000,
  keyboard_shortcuts_enabled: true,
  email_notifications: true,
  collaboration_notifications: true
};

export const useUserPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);

  const loadPreferences = useCallback(async () => {
    try {
      const userPrefs = await userPreferencesService.getPreferences();
      setPreferences(userPrefs || defaultPreferences);
    } catch (error) {
      console.error('Failed to load preferences:', error);
      setPreferences(defaultPreferences);
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePreferences = useCallback(async (newPreferences: Partial<UserPreferences>) => {
    const updatedPrefs = { ...preferences, ...newPreferences };
    setPreferences(updatedPrefs);
    
    try {
      await userPreferencesService.updatePreferences(newPreferences);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      // Revert on error
      setPreferences(preferences);
    }
  }, [preferences]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  return {
    preferences,
    updatePreferences,
    loading
  };
};
